import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../../environment";

const BASE_URL = `${API_BASE_PATH}/regularization`;

const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

// POST payload
export interface RegularizationCreate {
  attendance_id: number;
  reg_type: string;
  reason: string;
  notify: string | null;
}

// GET/PUT response object
export interface RegularizationRecord {
  reg_id: number;
  employee_id: string;
  attendance_id: number;
  check_in_time: string | null;
  check_out_time: string | null;
  timesheet_date: string;
  date_apply: string;
  reg_type: string;
  reason: string;
  reject_reason: string | null;
  status: "Pending" | "Approved" | "Rejected";
  approved_by: string | null;
  approved_date: string | null;
  created_at: string;
  updated_at: string | null;
}

// PUT payload
export interface RegularizationUpdate {
  reg_id: number;
  status: "Pending" | "Approved" | "Rejected";
  reject_reason?: string;
}

// Widgets response
export interface RegularizationWidgets {
  applied: number;
  approved: number;
  rejected: number;
  pending: number;
}

// 🌟 POST - Create regularization
export const postRegularization = async (
  data: RegularizationCreate,
  token: string
): Promise<RegularizationRecord> => {
  try {
    const formData = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value.toString());
    });

    const response = await axios.post(`${BASE_URL}/`, formData.toString(), {
      headers: {
        accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: `Bearer ${token}`,
      },
    });

    toast.success("Regularization submitted successfully");
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw { message };
  }
};

// 🔍 GET - Self regularizations
export const getSelfRegularizations = async (
  token: string
): Promise<RegularizationRecord[]> => {
  try {
    const response = await axios.get(`${BASE_URL}/self/`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw { message };
  }
};

// 🔍 GET - All regularizations
export const getAllRegularizations = async (
  token: string
): Promise<RegularizationRecord[]> => {
  try {
    const response = await axios.get(`${BASE_URL}/all/`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw { message };
  }
};

// 📊 GET - Regularization widgets
export const getRegularizationWidgets = async (
  month: number,
  year: number,
  day?: number | null,
  employee_id?: string | null,
  token?: string
): Promise<RegularizationWidgets> => {
  try {
    const params: any = { month, year };
    if (day !== undefined && day !== null) params.day = day;
    if (employee_id) params.employee_id = employee_id;

    const response = await axios.get(`${BASE_URL}/widgets`, {
      params,
      headers: {
        accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw { message };
  }
};

// ✅ PUT - Approve/Reject regularization
export const updateRegularizationStatus = async (
  updateData: RegularizationUpdate,
  token: string
): Promise<RegularizationRecord> => {
  try {
    const formData = new URLSearchParams();
    formData.append("reg_id", updateData.reg_id.toString());
    formData.append("status", updateData.status);
    if (updateData.reject_reason) {
      formData.append("reject_reason", updateData.reject_reason);
    }

    const response = await axios.put(`${BASE_URL}/`, formData.toString(), {
      headers: {
        accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: `Bearer ${token}`,
      },
    });

    toast.success(`Regularization ${updateData.status}`);
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw { message };
  }
};
